import React, { useEffect, useState, useRef } from "react";
import { SlTab, SlTabGroup, SlTabPanel, SlDrawer } from "@shoelace-style/shoelace/dist/react";
import { useTableState } from "../Designer/utils/useTableState";
import { ChatProvider, useChatContext } from "../../context/ChatContext";
import ChatPanel from "../Designer/components/ChatPanel";
import AssignedAds from "./AssignedAds";
import ProgressAds from "./ProgressAds";
import Completed from "./CompleteAds";
import api from "../../services/api";
import config from "../../config";
import { Toast } from "../../shared/Toast";
import PromotedImagePreview from "../../creativePreviews/PromotedImagePreview";
import PromotedVideoPreview from "../../creativePreviews/PromotedVideoPreview";
import HalfVideoPreview from "../../creativePreviews/HalfVideoPreview";
import RectangleClassifieds from "../../creativePreviews/RectangleClassifieds";
import { proxyDownload } from "../../utils/proxyDownload";
import Toggle from "../../utils/Toggle";
import Magnifier from "../../utils/Magnifier";
import { EmitEvent } from "../../services/socket";
import RotatableImage from "../Designer/utils/RotatableImage";
import { registerSW } from "../../utils/pushSubscription";

const getFileType = (url) => {
    const extension = url.split(".").pop()?.toLowerCase();
    const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
    return videoExtensions.includes(extension) ? "video" : "image";
};

const FullPreview = ({ images, currentIndex, isTextEditing, setIsTextEditing, setCurrentIndex, editingText, handleEditText, setEditingText, handleSaveText }) => {
    const current = images[currentIndex];

    const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    const handleNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);
    const [showMagnifier, setShowMagnifier] = useState(false);
    const [position, setPosition] = useState({ x: 50, y: 50 });
    const [isZoomed, setIsZoomed] = useState(false);
    const imgRef = useRef(null);

    const handleMouseMove = (e) => {
        const elem = imgRef.current;
        if (!elem) return;

        const { top, left, width, height } = elem.getBoundingClientRect();
        const x = ((e.pageX - left - window.pageXOffset) / width) * 100;
        const y = ((e.pageY - top - window.pageYOffset) / height) * 100;

        setPosition({ x, y });
    };

    return (
        <>
            <div className="flex flex-col items-center justify-center w-full h-[80vh] gap-4">
                <Toggle open={showMagnifier} setOpen={setShowMagnifier} title={"Zoom"} />
                <div className="relative flex items-center justify-center w-full min-h-[60%] max-h-[85%] bg-white rounded-lg shadow-md overflow-visible">
                    <button onClick={handlePrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Previous Image">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div className="relative bg-white rounded-lg shadow-lg p-2" onMouseMove={handleMouseMove}>
                        <RotatableImage
                            src={current?.url}
                            alt={`creative-${currentIndex}`}
                            className="max-w-[90%] max-h-[90%] object-cover cursor-zoom-in"
                            style={{ maxWidth: '90%', maxHeight: '90%' }}
                        />
                    </div>
                    {showMagnifier ? <Magnifier showMagnifier={showMagnifier} zoomLevel={5} url={current?.url} position={position} /> : null}
                    <button onClick={handleNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Next Image">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    <div
                        className={`absolute top-2 right-2 w-12 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${current.type === "video" ? "bg-purple-600" : "bg-blue-600"}`}
                    >
                        {current.type === "video" ? "VIDEO" : "IMG"}
                    </div>
                </div>

                <p className="text-xs text-gray-500">
                    Media <span className="font-medium">{currentIndex + 1}</span> of {images.length}
                </p>
            </div>

            {isZoomed && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center" onClick={() => setIsZoomed(false)}>
                    <img src={current.url} alt={`zoomed-${currentIndex}`} className="max-w-full max-h-full object-contain cursor-zoom-out" />
                </div>
            )}
        </>
    );
};

const ThumbnailPreview = ({ images, currentIndex, setCurrentIndex }) => (
    <div className="flex flex-wrap gap-2 my-2 py-1 bg-gray-50 border-t">
        {images.map((imageObj, idx) => (
            <div key={idx} className="relative">
                <img
                    src={imageObj.url}
                    alt={`thumb-${idx}`}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-12 h-12 object-cover rounded cursor-pointer border transition-transform ${idx === currentIndex ? "border-blue-500 scale-105" : "border-gray-300 hover:border-blue-300"}`}
                />
                <div className={`absolute top-0 right-0 px-1 text-xs font-bold text-white rounded-bl ${imageObj.type === "video" ? "bg-purple-600" : "bg-blue-600"}`}>
                    {imageObj.type === "video" ? "V" : "I"}
                </div>
            </div>
        ))}
    </div>
);

const WriteContentForm = ({ selectedForm, variations, handleVariationChange, setSelectedForm, adType }) => {
    const max_headline_limit = adType === "promoted_image_story" ? 75 : 32;
    const max_description_limit = adType === "promoted_image_story" ? 400 : 90;
    const show_headline = adType === "promoted_image_story" || adType === "classifiedads";

    return (
        <div className="flex flex-col gap-4 font-lg max-w-[70vh]">
            <div className="flex gap-2">
                {[1, 2, 3].map((num) => (
                    <button key={num} className={`px-3 py-1 rounded  ${selectedForm === num ? "bg-blue-600 text-white" : "bg-gray-200"}`} onClick={() => setSelectedForm(num)}>
                        Variation {num}
                    </button>
                ))}
            </div>

            {show_headline && (
                <div>
                    <label className="block text-xs  text-gray-700">Headline (Max {max_headline_limit} characters)</label>
                    <input
                        type="text"
                        value={variations[selectedForm - 1].headline}
                        onChange={(e) => handleVariationChange(selectedForm, "headline", e.target.value)}
                        placeholder="Headline"
                        className="w-full border px-2 py-1 rounded text-sm"
                        maxLength={max_headline_limit}
                    />
                    <div className="text-right  text-green-800">{max_headline_limit - variations[selectedForm - 1]?.headline?.trim().length} characters</div>
                </div>
            )}
            <div>
                <label className="block  font-medium text-gray-700">Description {show_headline && `(Max ${max_description_limit} characters)`}</label>
                <textarea
                    value={variations[selectedForm - 1].description}
                    onChange={(e) => handleVariationChange(selectedForm, "description", e.target.value)}
                    placeholder="Description"
                    rows="25"
                    className="w-full border max-h-[50vh] px-2 py-1 rounded"
                    {...(show_headline ? { maxLength: max_description_limit } : null)}
                />
                {show_headline && <div className="text-right  text-green-800">{max_description_limit - variations[selectedForm - 1]?.description?.trim().length} characters</div>}
            </div>
        </div>
    );
};

const ContentWriterHomeInner = () => {
    const { closeChat } = useChatContext();
    const [designs, setDesigns] = useState([]);
    const [loader, setLoader] = useState(false);
    const [totalAssigned, setTotalAssigned] = useState(0);
    const [totalProgress, setTotalProgress] = useState(0);
    const [totalComplete, setTotalComplete] = useState(0);
    const [currentChild, setCurrentChild] = useState("assigned-Ads");
    const [sortByLatestMessage, setSortByLatestMessage] = useState(() => {
        const saved = localStorage.getItem("view_content_writer_by_latest_message");
        return saved ? JSON.parse(saved) : false;
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [variations, setVariations] = useState([
        { id: 1, headline: "", description: "" },
        { id: 2, headline: "", description: "" },
        { id: 3, headline: "", description: "" },
    ]);
    const [selectedForm, setSelectedForm] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [adData, setAdData] = useState(null);
    const [mediaUrls, setMediaUrls] = useState([]);
    const [isTextEditing, setIsTextEditing] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [editingText, setEditingText] = useState({ url: null, text: "" });
    const [previewMode, setPreviewMode] = useState("full");
    const tabGroupRef = useRef(null);
    const { currentPage, setCurrentPage, handlePageChange } = useTableState();
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        registerSW();
    }, []);

    useEffect(() => {
        if (adData) setCurrentIndex(0);
    }, [adData]);

    const getMedia = async (creative_id) => {
        const result = await api({
            url: "/alpha_cst_designer/getMedia",
            method: "POST",
            data: { creative_id },
        });
        if (result.status === "SUCCESS") {
            const processed = result.data.map((item) => ({
                url: item.img_url,
                type: getFileType(item.img_url),
            }));
            setMediaUrls(processed);
        }
    };

    const fetchAdData = async (creative_id) => {
        try {
            const res = await api({ url: `/user/content_writer/get-ad-details`, method: "POST", data: { creative_id } });
            if (res.code === 200) setAdData(res.data[0]);
            await getMedia(creative_id);
        } catch (error) {
            Toast("error", "Failed to load ad details");
        }
    };

    const handleWriteContentClick = (row, formNumber) => {
        setSelectedRow(row);
        setSelectedForm(formNumber);
        fetchAdData(row.creative_id);
        setIsDrawerOpen(true);
    };

    const handleVariationChange = (id, field, value) => {
        if (field === "form") setSelectedForm(value);
        else setVariations((prev) => prev.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
    };

    const handleEditText = (url, currentText) => {
        setEditingText({ url, text: currentText });
    };

    const handleSaveText = async () => {
        if (!editingText.url || !selectedRow) return;

        try {
            const res = await api({
                url: `/user/content_writer/editText`,
                method: "POST",
                data: { creative_id: selectedRow.creative_id, url: editingText.url, text: editingText.text },
            });

            if (res.code === 200) {
                Toast("success", "Text updated successfully");
                setEditingText({ url: null, text: "" });
                setIsTextEditing(false);
                fetchAdData(selectedRow.creative_id);
            }
        } catch (error) {
            Toast("error", "Failed to update text");
        }
    };

    const hasValidContent = () => {
        if (selectedRow?.ad_type === "promoted_image_story" || selectedRow?.ad_type === "classifiedads") {
            return variations.some((v) => v.headline.trim() && v.description.trim());
        }
        return variations.some((v) => v.description.trim());
    };

    const handleUpload = async () => {
        if (!selectedRow || !hasValidContent()) {
            Toast("error", selectedRow?.ad_type === "promoted_image_story" || selectedRow?.ad_type === "classifiedads" ? "All fields required" : "Description required");
            return;
        }
        try {
            const res = await api({
                url: `/user/content_writer/uploadContent`,
                method: "POST",
                data: { content_id: selectedRow.content_id, creative_id: selectedRow.creative_id, variations },
            });
            if (res.status === "SUCCESS") {
                setIsDrawerOpen(false);
                if (currentChild === "progress-Ads") handleTabSwitch("complete-Ads");
                else getCompletedDesigns(currentPage);
                Toast("success", "Content uploaded successfully");
            } else {
                console.log(res);
                Toast("error", res?.data?.message || "Failed to upload content");
            }
        } catch (err) {
            Toast("error", err?.message);
        }
    };

    const totalCount = async () => {
        try {
            const res = await api({ url: `/user/content_writer/getTotalCount`, method: "POST", data: {} });
            if (res.code === 200) {
                setTotalAssigned(res.data.filter((item) => item.status === 0).reduce((sum, item) => sum + parseInt(item.total), 0));
                setTotalProgress(res.data.filter((item) => item.status === 1).reduce((sum, item) => sum + parseInt(item.total), 0));
                setTotalComplete(res.data.filter((item) => item.status === 2).reduce((sum, item) => sum + parseInt(item.total), 0));
            }
        } catch (error) {}
    };

    useEffect(() => {
        totalCount();
    }, [currentChild]);

    const closeModal = () => {
        setIsDrawerOpen(false);
        setAdData(null);
        setMediaUrls([]);
        setVariations([
            { id: 1, headline: "", description: "" },
            { id: 2, headline: "", description: "" },
            { id: 3, headline: "", description: "" },
        ]);
        setEditingText({ url: null, text: "", isOpen: false });
    };

    useEffect(() => {
        if (isDrawerOpen && selectedRow?.content?.length) setVariations(selectedRow.content);
    }, [isDrawerOpen, selectedRow]);

    const handleTabSwitch = (panelName) => {
        tabGroupRef.current?.show(panelName);
    };

    const getAssignedDesigns = async (page) => {
        setLoader(true);
        try {
            const res = await api({ url: `/user/content_writer/getAssignedDesigns`, method: "POST", data: { page, limit: config.PER_PAGE, sort_by_latest_message: sortByLatestMessage } });
            if (res.code === 200) setDesigns(res.data?.data);
        } catch (error) {}
        setLoader(false);
    };

    const getProgressDesigns = async (page) => {
        setLoader(true);
        try {
            const res = await api({ url: `/user/content_writer/getProgressedDesigns`, method: "POST", data: { page, limit: config.PER_PAGE, sort_by_latest_message: sortByLatestMessage } });
            if (res.code === 200) setDesigns(res.data?.data);
        } catch (error) {}
        setLoader(false);
    };

    const getCompletedDesigns = async (page) => {
        setLoader(true);
        try {
            const res = await api({ url: `/user/content_writer/getCompletedDesigns`, method: "POST", data: { page, limit: config.PER_PAGE, sort_by_latest_message: sortByLatestMessage } });
            if (res.code === 200) setDesigns(res.data?.data);
        } catch (error) {}
        setLoader(false);
    };

    const pickDesign = async (content_id, creative_id) => {
        try {
            const res = await api({ url: `/user/content_writer/pickDesign`, method: "POST", data: { content_id, creative_id } });
            if (res.code === 200) {
                Toast("success", "Design picked successfully");
                EmitEvent(creative_id, res.data?.log[0]);
                handleTabSwitch("progress-Ads");
            }
        } catch (error) {
            Toast("error", "Failed to pick design");
        }
    };
    const handleDownloadImages = async (images, filename) => {
        try {
            if (isDownloading) return;
            setIsDownloading(true);
            const imgUrls = images.map((image) => image.url);
            await proxyDownload(imgUrls, filename);
        } catch (error) {
            console.error(error);
        } finally {
            setIsDownloading(false);
        }
    };
    useEffect(() => {
        if (currentChild === "assigned-Ads") getAssignedDesigns(currentPage);
        else if (currentChild === "progress-Ads") getProgressDesigns(currentPage);
        else getCompletedDesigns(currentPage);
    }, [currentChild, currentPage, sortByLatestMessage]);

    useEffect(() => {
        localStorage.setItem("view_content_writer_by_latest_message", JSON.stringify(sortByLatestMessage));
    }, [sortByLatestMessage]);

    const handleSortByLatestMessageChange = (value) => {
        setSortByLatestMessage(!!value);
        setCurrentPage(0);
        closeChat();
    };

    return (
        <div className="p-4">
            <SlTabGroup ref={tabGroupRef} onSlTabShow={(event) => { setCurrentChild(event.detail.name); closeChat(); }}>
                <SlTab slot="nav" panel="assigned-Ads">
                    Assigned <span className="text-blue-600 mx-1">{totalAssigned || 0}</span>
                </SlTab>
                <SlTab slot="nav" panel="progress-Ads">
                    In Progress <span className="text-blue-600 mx-1">{totalProgress || 0}</span>
                </SlTab>
                <SlTab slot="nav" panel="complete-Ads">
                    Completed <span className="text-blue-600 mx-1">{totalComplete || 0}</span>
                </SlTab>
                <div className="flex items-center justify-end gap-2 my-3 text-xs font-semibold">
                    <Toggle open={sortByLatestMessage ? 1 : 0} setOpen={handleSortByLatestMessageChange} title={"Sort by Latest Message"} />
                </div>
                <SlTabPanel name="assigned-Ads">
                    <AssignedAds
                        Designs={designs}
                        setDesigns={setDesigns}
                        loader={loader}
                        setLoader={setLoader}
                        pickDesign={pickDesign}
                        totalAssigned={totalAssigned}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handlePageChange={handlePageChange}
                        activeTab={currentChild}
                    />
                </SlTabPanel>
                <SlTabPanel name="progress-Ads">
                    <ProgressAds
                        Designs={designs}
                        setDesigns={setDesigns}
                        loader={loader}
                        setLoader={setLoader}
                        handleWriteContentClick={handleWriteContentClick}
                        totalProgress={totalProgress}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handlePageChange={handlePageChange}
                        activeTab={currentChild}
                    />
                </SlTabPanel>
                <SlTabPanel name="complete-Ads">
                    <Completed
                        Designs={designs}
                        setDesigns={setDesigns}
                        loader={loader}
                        setLoader={setLoader}
                        handleWriteContentClick={handleWriteContentClick}
                        totalComplete={totalComplete}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handlePageChange={handlePageChange}
                        activeTab={currentChild}
                    />
                </SlTabPanel>
            </SlTabGroup>
            <SlDrawer label="Ad Preview & Content" style={{ "--size": "100vw" }} placement="end" open={isDrawerOpen} onSlAfterHide={closeModal}>
                <div className="flex h-full p-4 gap-4">
                    {adData ? (
                        (() => {
                            const previewProps = {
                                img_urls: "",
                                video_urls: mediaUrls.find((item) => item.type === "video")?.url || null,
                                head_line: adData?.head_line,
                                description: adData.description,
                                click_url: adData.click_url,
                                button_enabled: 0,
                                button_link: adData.click_url,
                                name: adData.creative_name,
                                position: adData.position,
                            };

                            return (
                                <>
                                    <div className="w-2/3 flex flex-col gap-2">
                                        {adData.creative_name && <h6>Campaign Name : {adData.creative_name}</h6>}
                                        <button
                                            className={`mx-3 px-2 py-1 rounded-lg shadow-md ${isDownloading ? "bg-green-500 cursor-progress" : "bg-blue-400"}`}
                                            onClick={() => handleDownloadImages(mediaUrls, adData.creative_name)}
                                            disabled={isDownloading}
                                        >
                                            Download Media
                                        </button>

                                        {mediaUrls.length > 0 && <ThumbnailPreview images={mediaUrls} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex} />}

                                        {mediaUrls.length > 0 && currentIndex !== null ? (
                                            <div className="relative">
                                                {adData.ad_creative_sub_type === "classified" && mediaUrls[currentIndex].type === "image" ? (
                                                    <RectangleClassifieds
                                                        preview={{
                                                            cla_brand_caption: adData.cla_brand_caption,
                                                            logo_urls: adData.cla_brand_img,
                                                            brand_name: adData.cla_brand_name,
                                                            btn_text_bgclr: adData.cla_btn_bg_clr,
                                                            btn_icon: adData.cla_btn_icon,
                                                            button_text: adData.cla_btn_txt,
                                                            btn_text_clr: adData.cla_btn_txt_col,
                                                            btn_url: adData.cla_btn_url,
                                                            no_of_rec: adData.cla_no_of_rec,
                                                            sub_type: adData.cla_sub_type,
                                                            img_urls: mediaUrls[currentIndex].url,
                                                            head_line: adData?.head_line || adData.headline,
                                                            description: adData.description,
                                                            button_enabled: !!adData.cla_btn_txt,
                                                        }}
                                                    />
                                                ) : adData.ad_creative_sub_type === "promoted_video_story" || adData.ad_creative_sub_type === "promoted_image_story" ? (
                                                    mediaUrls[currentIndex].type === "video" ? (
                                                        <PromotedVideoPreview preview={{ ...previewProps, img_urls: mediaUrls[currentIndex].url }} />
                                                    ) : (
                                                        <PromotedImagePreview preview={{ ...previewProps, img_urls: mediaUrls[currentIndex].url }} />
                                                    )
                                                ) : adData.ad_creative_sub_type === "half_video" ? (
                                                    <HalfVideoPreview preview={{ ...previewProps, img_urls: mediaUrls[currentIndex].url }} />
                                                ) : mediaUrls[currentIndex].type === "image" ? (
                                                    <FullPreview
                                                        images={mediaUrls}
                                                        currentIndex={currentIndex}
                                                        setCurrentIndex={setCurrentIndex}
                                                        handleEditText={handleEditText}
                                                        editingText={editingText}
                                                        setEditingText={setEditingText}
                                                        handleSaveText={handleSaveText}
                                                        isTextEditing={isTextEditing}
                                                        setIsTextEditing={setIsTextEditing}
                                                    />
                                                ) : (
                                                    <video src={mediaUrls[currentIndex].url} controls className="w-full h-full object-cover" />
                                                )}
                                                {mediaUrls.length > 1 && (
                                                    <>
                                                        <button
                                                            onClick={() => setCurrentIndex((p) => (p - 1 + mediaUrls.length) % mediaUrls.length)}
                                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg z-10"
                                                            aria-label="Previous"
                                                        >
                                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => setCurrentIndex((p) => (p + 1) % mediaUrls.length)}
                                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg z-10"
                                                            aria-label="Next"
                                                        >
                                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="font-bold text-lg text-center flex items-center justify-center w-full h-64 border rounded bg-gray-100 text-gray-500">NO MEDIA FOUND</div>
                                        )}
                                    </div>
                                    <div className="w-1/3 p-4 bg-gray-50 rounded-lg flex flex-col gap-4">
                                        <h4 className="font-semibold">Content Writing</h4>
                                        <WriteContentForm
                                            selectedForm={selectedForm}
                                            variations={variations}
                                            setSelectedForm={setSelectedForm}
                                            handleVariationChange={handleVariationChange}
                                            adType={selectedRow?.ad_creative_sub_type}
                                        />
                                        <button onClick={handleUpload} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                                            Upload Content
                                        </button>
                                    </div>
                                </>
                            );
                        })()
                    ) : (
                        <div className="flex items-center justify-center h-full w-full">
                            <p className="text-gray-500">Loading...</p>
                        </div>
                    )}
                </div>
            </SlDrawer>
            <ChatPanel />
        </div>
    );
};

const ContentWriterHome = () => (
    <ChatProvider>
        <ContentWriterHomeInner />
    </ChatProvider>
);

export default ContentWriterHome;
